// react
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// mui
import { Button } from '@mui/material';

// components
import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import AdminTablePage from '@karehero/llama/pwa/templates/AdminTablePage/AdminTablePage';
import DataTable, {
  GridColDef,
} from '@karehero/llama/pwa/organisms/DataTable/DataTable';

// store
import { useGetAllOrganizationQuery } from 'store/api/organization';
import { Organization, PaginationParams } from '@karehero/models';

// columns
const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  {
    field: 'organizationType',
    headerName: 'Type',
    flex: 0.7,
    minWidth: 120,
    valueGetter: (_value: any, row: any) => row.organizationType?.name || '',
  },
  {
    field: 'partnerName',
    headerName: 'Referring Partner',
    flex: 0.7,
    minWidth: 150,
    valueGetter: (_value: any, row: any) => row.partnerName ?? '—',
  },
];

/**
 * OrganizationTable is the dashboard page.
 */
export const OrganizationTable = () => {
  // hooks
  const navigate = useNavigate();
  const [paginationParams, setPaginationParams] = useState<PaginationParams>({
    page: 1,
    perPage: 10,
  });
  const {
    data: organizationsData,
    isFetching,
    isError,
  } = useGetAllOrganizationQuery(paginationParams);

  // memo
  const rows = useMemo(() => {
    if (!organizationsData?.data) return [];
    return organizationsData.data.map((organization) => ({
      ...organization,
    }));
  }, [organizationsData]);

  // pagination handlers
  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      setPaginationParams((prev) => ({
        ...prev,
        page: page + 1,
        perPage: pageSize,
      }));
    },
    [],
  );

  const handleSearchChange = useCallback((search: string) => {
    setPaginationParams((prev) => ({ ...prev, search, page: 1 }));
  }, []);

  return (
    <AdminTablePage title='Organisations'>
      <DataTable
        columns={columns}
        rows={rows}
        paginationMode='server'
        rowCount={organizationsData?.pagination?.totalItems}
        page={(paginationParams.page ?? 1) - 1}
        onPaginationChange={handlePaginationChange}
        rowsPerPage={paginationParams.perPage ?? 10}
        isLoading={isFetching}
        isError={isError}
        onSearchChange={handleSearchChange}
        searchValue={paginationParams.search ?? ''}
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            onPress: (org: Organization) =>
              navigate(`/admin/organization/${org.id}/edit`),
          },
          {
            label: 'View Members',
            icon: 'users',
            onPress: (org: Organization) =>
              navigate(`/admin/organization/${org.id}/member`),
          },
        ]}
        toolbarActions={
          <Button
            variant='contained'
            size='small'
            onClick={() => navigate('/admin/organization/new')}
            startIcon={<Icon icon='plus' size='sm' />}
          >
            New Organisation
          </Button>
        }
      />
    </AdminTablePage>
  );
};

export default OrganizationTable;
